import type { Form } from 'vee-validate'
import { isNullOrUndefined } from '@/utils/common.utils'

export type FieldErrorNode = string | FieldErrorNode[] | { [key: string]: FieldErrorNode }
export type FieldErrorTree = Record<string, FieldErrorNode>

function isFieldErrorTree(data: unknown): data is FieldErrorTree {
  return typeof data === 'object' && data !== null && !Array.isArray(data)
}

// Flattens a DRF error tree into vee-validate field paths, e.g.
// { cargo: { items: [{ stackable: ['…'] }] } } → { 'cargo.items[0].stackable': '…' }.
// Paths stay raw (snake_case) — camelization happens at the call site so an
// errorFieldMap keyed by the raw backend name still matches.
export function flattenFieldErrors(data: FieldErrorTree): Record<string, string> {
  if (!isFieldErrorTree(data))
    return {}

  const out: Record<string, string> = {}

  walkFieldErrors(data, '', out)

  return out
}

// Untrusted network input: a malformed node (null, number) is ignored, not thrown on.
function walkFieldErrors(node: FieldErrorNode | null | undefined, path: string, out: Record<string, string>): void {
  if (isNullOrUndefined(node))
    return

  if (typeof node === 'string') {
    appendFieldError(out, path, node)

    return
  }

  if (Array.isArray(node)) {
    if (node.every(item => typeof item === 'string')) {
      appendFieldError(out, path, node.filter(Boolean).join(' '))

      return
    }

    node.forEach((item, index) => walkFieldErrors(item, `${path}[${index}]`, out))

    return
  }

  if (typeof node === 'object') {
    for (const [key, value] of Object.entries(node))
      walkFieldErrors(value, path ? `${path}.${key}` : key, out)
  }
}

function appendFieldError(out: Record<string, string>, path: string, message: string): void {
  if (!path || !message)
    return

  out[path] = out[path] ? `${out[path]} ${message}` : message
}

// Pulls the first human-readable message out of a DRF error payload (e.g. { type: ['…'] },
// { detail: '…' } or { nonFieldErrors: ['…'] }) so it can be surfaced in a toast. Only object
// bodies are accepted — plain strings (HTML 500 pages, raw text) fall through as undefined so
// the caller can show a generic message instead of dumping the blob into a toast.
export function firstApiErrorMessage(data: unknown): string | undefined {
  if (!isFieldErrorTree(data))
    return undefined

  const messages = Object.values(flattenFieldErrors(data))

  return messages.find(message => !!message)
}

export function formContainsOnlyNonFieldErrors(form: typeof Form): boolean {
  const errorsKeys = Object.keys(form?.getErrors() || {})

  return errorsKeys.length === 1 && errorsKeys[0] === 'nonFieldErrors'
}

type FormErrors = Partial<Record<string, string>>
export function resetFormErrorsAfterRemove(errors: FormErrors, prefix: string, removeIndex: number): { emptyErrors: FormErrors; newErrors: Partial<Record<string, string>> } {
  const emptyErrors = createEmptyErrors(errors, prefix)
  const currentErrorsKeys = Object.keys(emptyErrors)
  const prefixWithIndex = (i: number) => `${prefix}[${i}]`

  const newErrors = currentErrorsKeys.reduce<FormErrors>((maps, key, index) => {
    if (!key.startsWith(prefixWithIndex(removeIndex))) {
      const newKey = index > removeIndex ? key.replace(prefixWithIndex(index), prefixWithIndex(index - 1)) : key

      maps[newKey] = errors[key]
    }

    return maps
  }, {})

  return { emptyErrors, newErrors }
}

export function createEmptyErrors(errors: FormErrors, prefix: string): FormErrors {
  const currentErrorsKeys = Object.keys(errors).filter(key => key.startsWith(prefix))

  return currentErrorsKeys.reduce<FormErrors>((maps, key) => ({ ...maps, [key]: '' }), {})
}
